import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'
import { useState, useCallback, useEffect, useMemo, useRef, type MouseEvent } from 'react'
import { Link, router, useForm, usePage } from '@inertiajs/react'
import {
  AlertTriangle,
  Check,
  ClipboardList,
  Eraser,
  Loader2,
  Minus,
  PowerOff,
  Trash2,
  X,
} from 'lucide-react'
import { Checkbox } from '@/shared/ui/checkbox'
import { Card, CardContent, CardHeader } from '@/shared/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger } from '@/shared/ui/tooltip'
import { ExplanationForm } from './ExplanationForm'
import { ResolveForm } from './ResolveForm'
import type { DataFieldEntry, DataFieldFile } from './ResolveForm'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { FrequencyContextPanel } from '@/shared/ui/frequency-context-panel'
import { formatRecordedInstantWithZone, formatTimelineDotDate } from '@/shared/lib/tenantDateTime'
import { t } from '@/shared/lib/i18n'
import { tasksDashboardDataFieldsPath } from '@/shared/lib/routes'
import apiClient from '@/shared/lib/apiClient'
import { useTaskNotes } from '@/shared/hooks/useTaskNotes'
import type WebNonComplianceCard from '@/shared/types/web/WebNonComplianceCard'
import type WebInstrumentsTaskLogDataField from '@/shared/types/web/WebInstrumentsTaskLogDataField'
import type { SharedProps } from '@/shared/types/SharedProps'
import TaskDayOccurrencesDialog, {
  type TaskDayOccurrencesModalState,
} from '@/pages/Instruments/components/ReportsTab/TaskDayOccurrencesDialog'
import { outlineButtonClassFor } from '@/shared/constants/statusPalette'

export type TimelineDot = WebNonComplianceCard['timeline'][number]

const ncDataFieldsCache = new Map<number, WebInstrumentsTaskLogDataField[]>()

// ─── ActionCard ───────────────────────────────────────────────────────────────

interface ActionCardProps {
  icon: React.ReactNode
  title: string
  subtitle: string
  colorClass: string
  onClick?: () => void
  href?: string
  disabled?: boolean
}

const ActionCard = ({
  icon,
  title,
  subtitle,
  colorClass,
  onClick,
  href,
  disabled,
}: ActionCardProps) => {
  const className = `flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${colorClass} ${disabled ? 'pointer-events-none opacity-50' : ''}`

  if (href) {
    return (
      <Link href={href} className={className}>
        {icon}
        <span className="text-xs font-semibold">{title}</span>
        <span className="text-xs opacity-75">{subtitle}</span>
      </Link>
    )
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick?.()
        }
      }}
    >
      {icon}
      <span className="text-xs font-semibold">{title}</span>
      <span className="text-xs opacity-75">{subtitle}</span>
    </button>
  )
}

// ─── DotIcon ──────────────────────────────────────────────────────────────────

const DotIcon = ({ dot }: { dot: TimelineDot }) => {
  // Decorative: parent TimelineDotGlyph supplies role="img" + aria-label.
  if (dot.value === 100) return <Check className="h-3 w-3" aria-hidden />
  // 367: override (ODE) gets its own glyph — the letter O (legacy parity), distinct
  // from No Testing (NPT) which keeps the dash.
  if (dot.reviewStatusCode === 'ODE')
    return (
      <span className="text-xs font-bold leading-none" aria-hidden>
        O
      </span>
    )
  if (dot.reviewStatusCode === 'NPT') return <Minus className="h-3 w-3" aria-hidden />
  return <X className="h-3 w-3" aria-hidden />
}

// ─── TimelineStrip ────────────────────────────────────────────────────────────

const dotClassName = (dot: TimelineDot): string => {
  const base =
    'inline-flex h-7 w-7 cursor-default items-center justify-center rounded-md border-2 shadow-sm'
  if (dot.value === 100) return `${base} bg-white border-emerald-500 text-emerald-600`
  if (dot.reviewStatusCode === 'NPT' || dot.reviewStatusCode === 'ODE')
    return `${base} bg-white border-slate-400 text-slate-500`
  if (dot.isCurrent)
    return `${base} bg-red-600 border-red-700 text-white ring-2 ring-white ring-offset-1`
  return `${base} bg-white border-red-500 text-red-600`
}

interface TimelineStripProps {
  dots: TimelineDot[]
  intraday: boolean
  tenantTimeFormat: number | null | undefined
  onDotClick?: (dot: TimelineDot) => void
}

interface TimelineDotGlyphProps {
  dot: TimelineDot
  tenantTimeFormat: number | null | undefined
  onDotClick?: (dot: TimelineDot) => void
}

const TimelineDotGlyph = ({ dot, onDotClick }: TimelineDotGlyphProps) => {
  const { formatTooltip } = useSiteDateTime()
  const tooltipLabel = formatTooltip(dot.date)
  const glyphClassName = dotClassName(dot).replace('cursor-default', 'cursor-pointer')

  const glyph = onDotClick ? (
    <button
      type="button"
      className={glyphClassName}
      aria-label={tooltipLabel}
      onClick={() => onDotClick(dot)}
    >
      <DotIcon dot={dot} />
    </button>
  ) : (
    <span className={dotClassName(dot)} role="img" aria-label={tooltipLabel}>
      <DotIcon dot={dot} />
    </span>
  )

  return (
    <TooltipRoot>
      <TooltipTrigger asChild>{glyph}</TooltipTrigger>
      <TooltipContent>{tooltipLabel}</TooltipContent>
    </TooltipRoot>
  )
}

const stopTimelineSelectPropagation = (event: MouseEvent): void => {
  event.stopPropagation()
}

const TimelineStrip = ({ dots, intraday, tenantTimeFormat, onDotClick }: TimelineStripProps) => {
  const stripBody = intraday ? (
    <>
      <p className="text-xs font-medium text-muted-foreground">
        {t('nonCompliance.timeline.heading')}
      </p>
      <div className="flex gap-3">
        {(['prev', 'current', 'next'] as const)
          .map((dayTag) => dots.filter((timelineDot) => timelineDot.day === dayTag))
          .filter((col) => col.length > 0)
          .map((colDots) => (
            <div key={colDots[0].requiredComplianceId} className="flex flex-col items-center gap-1">
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {formatTimelineDotDate(colDots[0].date)}
              </span>
              {colDots.map((dot) => (
                <TimelineDotGlyph
                  key={dot.requiredComplianceId}
                  dot={dot}
                  tenantTimeFormat={tenantTimeFormat}
                  onDotClick={onDotClick}
                />
              ))}
            </div>
          ))}
      </div>
    </>
  ) : (
    <>
      <p className="text-xs font-medium text-muted-foreground">
        {t('nonCompliance.timeline.heading')}
      </p>
      <div className="flex flex-wrap gap-2">
        {dots.map((dot) => (
          <div key={dot.requiredComplianceId} className="flex flex-col items-center gap-0.5">
            <span className="whitespace-nowrap text-xs text-muted-foreground">
              {formatTimelineDotDate(dot.date)}
            </span>
            <TimelineDotGlyph
              dot={dot}
              tenantTimeFormat={tenantTimeFormat}
              onDotClick={onDotClick}
            />
          </div>
        ))}
      </div>
    </>
  )

  // Shared provider for the strip; stopPropagation so select-mode card clicks are not toggled by tooltip/dot interaction.
  return (
    <TooltipProvider>
      <div className="space-y-1" onClick={stopTimelineSelectPropagation}>
        {stripBody}
      </div>
    </TooltipProvider>
  )
}

// ─── RequireExplanationDialog ─────────────────────────────────────────────────

interface RequireExplanationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  heading: string
  warningPrimary: string
  warningSecondary?: string
  commentsPlaceholder: string
  submitLabel: string
  cancelLabel: string
  onSubmit: (comments: string) => void
  processing: boolean
}

export const RequireExplanationDialog = ({
  open,
  onOpenChange,
  heading,
  warningPrimary,
  warningSecondary,
  commentsPlaceholder,
  submitLabel,
  cancelLabel,
  onSubmit,
  processing,
}: RequireExplanationDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{heading}</DialogTitle>
        <DialogDescription className="sr-only">{warningPrimary}</DialogDescription>
      </DialogHeader>

      <ExplanationForm
        warningPrimary={warningPrimary}
        warningSecondary={warningSecondary}
        commentsPlaceholder={commentsPlaceholder}
        submitLabel={submitLabel}
        cancelLabel={cancelLabel}
        onSubmit={onSubmit}
        onCancel={() => onOpenChange(false)}
        processing={processing}
      />
    </DialogContent>
  </Dialog>
)

// ─── ResolveDialog ────────────────────────────────────────────────────────────

interface ResolveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  record: WebNonComplianceCard
  processing: boolean
  dataFields: WebInstrumentsTaskLogDataField[] | null
  dataFieldsLoading: boolean
  taskNotes: string | null
  serverCompletedAtError?: string | null
  onSubmit: (
    completedAt: string,
    comments: string,
    dataFieldEntries: DataFieldEntry[],
    dataFieldFiles: DataFieldFile[],
  ) => void
}

export const ResolveDialog = ({
  open,
  onOpenChange,
  record,
  processing,
  dataFields,
  dataFieldsLoading,
  taskNotes,
  serverCompletedAtError = null,
  onSubmit,
}: ResolveDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{t('nonCompliance.dialogs.resolve.heading')}</DialogTitle>
        <DialogDescription className="sr-only">
          {t('nonCompliance.dialogs.resolve.fillTheHole')}
        </DialogDescription>
      </DialogHeader>

      {dataFieldsLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <ResolveForm
          date={record.date}
          missedTime={record.missedTime}
          windowStartTime={record.windowStartTime}
          siteTimeZone={record.timeZoneName}
          taskNotes={taskNotes}
          dataFields={dataFields ?? undefined}
          onSubmit={onSubmit}
          onCancel={() => onOpenChange(false)}
          processing={processing}
          serverCompletedAtError={serverCompletedAtError}
        />
      )}
    </DialogContent>
  </Dialog>
)

// ─── NonComplianceCard ────────────────────────────────────────────────────────

export interface NonComplianceCardProps {
  record: WebNonComplianceCard
  isDismissing: boolean
  onActionStart: (id: number) => void
  selectMode: boolean
  selected: boolean
  onToggleSelect: (id: number) => void
  canReview: boolean
  /** When true, the 4 resolution action buttons are hidden entirely. Use on read-only contexts. */
  hideActions?: boolean
  /** Clear Offline stays available on the Non-Compliance tab. Compliance Reviews passes false. */
  allowOfflineRetire?: boolean
  hideInstrumentHeader?: boolean
  /** Default true. Task-queue-like surfaces (Dashboard/Tasks) must pass false — legacy DASHBOARD_INSTRUMENTS hides timeline. */
  showTimeline?: boolean
  /**
   * Called after any review action (Resolve/No Testing/Ignore/Confirm) succeeds. Lets a
   * parent page refresh other stale, non-Inertia-prop state for this record's instrument —
   * e.g. Dashboard/Tasks' embedded InstrumentCalendarViewPanel, which fetches and caches its
   * own calendar data outside the Inertia props this review action's response updates
   * (REBUILD-409: the calendar didn't refresh after an NC review without a manual reload).
   */
  onReviewSuccess?: () => void
}

export const NonComplianceCard = ({
  record,
  isDismissing,
  onActionStart,
  selectMode,
  selected,
  onToggleSelect,
  canReview,
  hideActions = false,
  allowOfflineRetire = true,
  hideInstrumentHeader = false,
  showTimeline = true,
  onReviewSuccess,
}: NonComplianceCardProps) => {
  const { tenantTimeFormat } = usePage<SharedProps>().props
  const isCalendar = record.fixedSchedule === true
  const location = [record.siteName, record.departmentName, record.unitName]
    .filter(Boolean)
    .join(' - ')

  const taskLabel = t('nonCompliance.card.taskLabel')

  const [resolveOpen, setResolveOpen] = useState(false)
  const [ignoreOpen, setIgnoreOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [offlineRetireOpen, setOfflineRetireOpen] = useState(false)
  const [resolveServerCompletedAtError, setResolveServerCompletedAtError] = useState<string | null>(
    null,
  )

  const ntForm = useForm({ review_status_code: 'NPT' })
  const [resolveProcessing, setResolveProcessing] = useState(false)
  const [ignoreProcessing, setIgnoreProcessing] = useState(false)
  const [confirmProcessing, setConfirmProcessing] = useState(false)
  const [offlineRetireProcessing, setOfflineRetireProcessing] = useState(false)

  const [dataFields, setDataFields] = useState<WebInstrumentsTaskLogDataField[] | null>(null)
  const [dataFieldsLoading, setDataFieldsLoading] = useState(false)
  const [dayOccurrencesModalState, setDayOccurrencesModalState] =
    useState<TaskDayOccurrencesModalState | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const taskNotes = useTaskNotes(
    record.instrumentTaskId,
    record.hasTaskNotes && (resolveOpen || dayOccurrencesModalState !== null),
  )

  // Keys are YYYY-MM-DD dates present in the timeline — values are unused (dialog only reads Object.keys).
  // dot.date is a full ISO datetime; slice to the date portion for the dialog's dayOfMonth helper.
  const timelineDateStripMap = useMemo((): Record<string, number> => {
    const result: Record<string, number> = {}
    for (const dot of record.timeline) {
      result[dot.date.slice(0, 10)] ??= 0
    }
    return result
  }, [record.timeline])

  const fetchDataFields = useCallback((instrumentTaskId: number) => {
    if (ncDataFieldsCache.has(instrumentTaskId)) {
      setDataFields(ncDataFieldsCache.get(instrumentTaskId)!)
      return
    }

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setDataFieldsLoading(true)

    apiClient
      .get(tasksDashboardDataFieldsPath(instrumentTaskId), { signal: controller.signal })
      .then((response) => {
        const fields = response.data as WebInstrumentsTaskLogDataField[]
        ncDataFieldsCache.set(instrumentTaskId, fields)
        setDataFields(fields)
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          console.error('Failed to load NC data fields', error)
          setDataFields([])
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setDataFieldsLoading(false)
      })
  }, [])

  useEffect(() => () => abortRef.current?.abort(), [])

  const handleDotClick = useCallback(
    (dot: TimelineDot) => {
      if (record.instrumentTaskId == null) return

      setDayOccurrencesModalState({
        instrumentTaskId: record.instrumentTaskId,
        taskName: record.taskName ?? '',
        taskNotes: null,
        date: dot.date.slice(0, 10),
        intervalTypeDescription: record.intervalTypeDescription,
        dataFields: dataFields ?? [],
      })

      if (dataFields === null) {
        fetchDataFields(record.instrumentTaskId)
      }
    },
    [dataFields, fetchDataFields, record],
  )

  const handleTimelineReviewSuccess = useCallback(() => {
    setDayOccurrencesModalState(null)
    onActionStart(record.id)
    router.reload()
  }, [onActionStart, record.id])

  const handleResolveOpen = useCallback(() => {
    setResolveServerCompletedAtError(null)
    setResolveOpen(true)
    if (dataFields === null && record.instrumentTaskId != null) {
      fetchDataFields(record.instrumentTaskId)
    }
  }, [dataFields, record.instrumentTaskId, fetchDataFields])

  const handleNpt = () => {
    if (!record.reviewHref || ntForm.processing) return
    ntForm.post(record.reviewHref, {
      preserveScroll: true,
      onStart: () => onActionStart(record.id),
      onSuccess: onReviewSuccess,
    })
  }

  const handleResolveSubmit = (
    completedAt: string,
    comments: string,
    dataFieldEntries: DataFieldEntry[],
    dataFieldFiles: DataFieldFile[],
  ) => {
    if (!record.resolvePostHref || resolveProcessing) return
    // Keep the dialog open through the post so a refused in-offline (or out-of-window)
    // resolve can surface errors.completedAt instead of closing first (REBUILD-461).
    setResolveServerCompletedAtError(null)
    onActionStart(record.id)
    router.post(
      record.resolvePostHref,
      {
        completed_at: completedAt,
        comments,
        data_field_entries: dataFieldEntries,
        data_field_files: dataFieldFiles,
      },
      {
        preserveScroll: true,
        onStart: () => setResolveProcessing(true),
        onSuccess: () => {
          setResolveOpen(false)
          onReviewSuccess?.()
        },
        onError: (errors) => {
          const raw = errors.completedAt
          const message = Array.isArray(raw) ? raw[0] : raw
          if (message) setResolveServerCompletedAtError(String(message))
        },
        onFinish: () => setResolveProcessing(false),
      },
    )
  }

  const handleIgnoreSubmit = (comments: string) => {
    if (!record.reviewHref || ignoreProcessing) return
    setIgnoreOpen(false)
    onActionStart(record.id)
    router.post(
      record.reviewHref,
      { review_status_code: 'ODE', comments },
      {
        preserveScroll: true,
        onStart: () => setIgnoreProcessing(true),
        onSuccess: onReviewSuccess,
        onFinish: () => setIgnoreProcessing(false),
      },
    )
  }

  const handleConfirmSubmit = (comments: string) => {
    if (!record.reviewHref || confirmProcessing) return
    setConfirmOpen(false)
    onActionStart(record.id)
    router.post(
      record.reviewHref,
      { review_status_code: 'CNM', comments },
      {
        preserveScroll: true,
        onStart: () => setConfirmProcessing(true),
        onSuccess: onReviewSuccess,
        onFinish: () => setConfirmProcessing(false),
      },
    )
  }

  const handleOfflineRetireSubmit = (comments: string) => {
    if (!record.reviewHref || offlineRetireProcessing) return
    setOfflineRetireOpen(false)
    onActionStart(record.id)
    router.post(
      record.reviewHref,
      { review_status_code: 'OFL', comments },
      {
        preserveScroll: true,
        onStart: () => setOfflineRetireProcessing(true),
        onSuccess: onReviewSuccess,
        onFinish: () => setOfflineRetireProcessing(false),
      },
    )
  }

  return (
    <div
      className={`overflow-hidden transition-all duration-500 ease-in-out ${isDismissing ? 'opacity-0 max-h-0' : 'opacity-100 max-h-[800px]'}`}
    >
      <Card
        className={`border-neutral-200 bg-neutral-50 ${selectMode ? 'cursor-pointer' : ''}`}
        onClick={selectMode ? () => onToggleSelect(record.id) : undefined}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            {selectMode && (
              <Checkbox
                checked={selected}
                onCheckedChange={() => onToggleSelect(record.id)}
                onClick={(event) => event.stopPropagation()}
                aria-label={record.instrumentLabel}
                className="mt-0.5 shrink-0"
              />
            )}
            <AlertTriangle className="h-4 w-4 shrink-0 text-neutral-500" />
            <span className="font-semibold text-sm">{t('nonCompliance.card.heading')}</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 min-w-0 break-words">
          {!hideInstrumentHeader && (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold truncate">{record.instrumentLabel}</p>
                <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-muted-foreground">
                  {record.serialNumber && (
                    <span>
                      {t('nonCompliance.card.serialNumber')} {record.serialNumber}
                    </span>
                  )}
                  <span>
                    {t('nonCompliance.card.ellId')} {record.instrumentId}
                  </span>
                  {location && (
                    <span>
                      {t('nonCompliance.card.location')}: {location}
                    </span>
                  )}
                </div>
              </div>
              <div
                className="flex shrink-0 flex-col gap-1 text-right text-sm"
                onClick={(event) => event.stopPropagation()}
              >
                <Link
                  href={record.viewInstrumentHref}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {t('nonCompliance.card.viewInstrument')}
                </Link>
                <Link
                  href={record.viewMaintenanceLogsHref}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {t('nonCompliance.card.viewMaintenanceLogs')}
                </Link>
              </div>
            </div>
          )}

          {record.taskName && (
            <div className="text-sm">
              <span>{taskLabel}:</span> <span className="font-bold">{record.taskName}</span>
            </div>
          )}

          {record.date && (
            <div className="text-sm">
              <span>{t('nonCompliance.card.missedDate')}:</span>{' '}
              <span className="font-bold">
                {formatRecordedInstantWithZone(
                  record.missedTime,
                  record.date,
                  record.timeZoneName,
                  tenantTimeFormat,
                )}
              </span>
            </div>
          )}

          {(record.intervalTypeDescription || record.frequencyTypeCode) && (
            <div className="space-y-1 text-sm">
              <div>
                <span>
                  {isCalendar
                    ? t('nonCompliance.card.calendarFrequency')
                    : t('nonCompliance.card.frequency')}
                  :
                </span>{' '}
                <span className="font-bold">{record.intervalTypeDescription || ''}</span>
              </div>
              <FrequencyContextPanel
                frequencyTypeCode={record.frequencyTypeCode}
                intervalTypeDescription={record.intervalTypeDescription}
                frequencyRrule={record.frequencyRrule}
                leadTimeIntervalHours={record.leadTimeIntervalHours}
                leadTimeUnit={record.leadTimeUnit}
                lateTimeIntervalHours={record.lateTimeIntervalHours}
                lateTimeUnit={record.lateTimeUnit}
                fixedScheduleStartTime={record.fixedScheduleStartTime}
                endsAtMidnight={record.endsAtMidnight}
                hideDescriptionInSummary
              />
            </div>
          )}

          {!canReview && <NoAccessNotice />}

          {!hideActions && !selectMode && (
            <div
              className={`grid grid-cols-2 gap-2 ${allowOfflineRetire && record.offlineRetireEligible ? 'sm:grid-cols-3' : 'sm:grid-cols-4'}`}
              data-testid="non-compliance-actions"
            >
              <ActionCard
                icon={<ClipboardList className="h-5 w-5" />}
                title={t('nonCompliance.actions.resolve.title')}
                subtitle={t('nonCompliance.actions.resolve.subtitle')}
                colorClass={outlineButtonClassFor('blue', true)}
                onClick={handleResolveOpen}
                disabled={!canReview || resolveProcessing || !record.resolvePostHref}
              />

              <ActionCard
                icon={<Eraser className="h-5 w-5" />}
                title={t('nonCompliance.actions.npt.title')}
                subtitle={t('nonCompliance.actions.npt.subtitle')}
                colorClass={outlineButtonClassFor('grey')}
                onClick={handleNpt}
                disabled={!canReview || ntForm.processing || !record.reviewHref}
              />

              <ActionCard
                icon={<Trash2 className="h-5 w-5" />}
                title={t('nonCompliance.actions.ignore.title')}
                subtitle={t('nonCompliance.actions.ignore.subtitle')}
                colorClass={outlineButtonClassFor('yellow')}
                onClick={() => setIgnoreOpen(true)}
                disabled={!canReview || !record.reviewHref}
              />

              <ActionCard
                icon={<AlertTriangle className="h-5 w-5" />}
                title={t('nonCompliance.actions.confirm.title')}
                subtitle={t('nonCompliance.actions.confirm.subtitle')}
                colorClass={outlineButtonClassFor('red')}
                onClick={() => setConfirmOpen(true)}
                disabled={!canReview || !record.reviewHref}
              />

              {allowOfflineRetire && record.offlineRetireEligible && (
                <ActionCard
                  icon={<PowerOff className="h-5 w-5" />}
                  title={t('nonCompliance.actions.offlineRetire.title')}
                  subtitle={t('nonCompliance.actions.offlineRetire.subtitle')}
                  colorClass={outlineButtonClassFor('grey')}
                  onClick={() => setOfflineRetireOpen(true)}
                  disabled={!canReview || offlineRetireProcessing || !record.reviewHref}
                />
              )}
            </div>
          )}

          {showTimeline && record.timeline.length > 0 && (
            <TimelineStrip
              dots={record.timeline}
              intraday={record.intradayTimeline}
              tenantTimeFormat={tenantTimeFormat}
              onDotClick={record.instrumentTaskId != null ? handleDotClick : undefined}
            />
          )}
        </CardContent>

        <ResolveDialog
          open={resolveOpen}
          onOpenChange={(open) => {
            setResolveOpen(open)
            if (!open) setResolveServerCompletedAtError(null)
          }}
          record={record}
          processing={resolveProcessing}
          dataFields={dataFields}
          dataFieldsLoading={dataFieldsLoading}
          taskNotes={taskNotes}
          serverCompletedAtError={resolveServerCompletedAtError}
          onSubmit={handleResolveSubmit}
        />

        <RequireExplanationDialog
          open={ignoreOpen}
          onOpenChange={setIgnoreOpen}
          heading={t('nonCompliance.dialogs.ignore.heading')}
          warningPrimary={t('nonCompliance.dialogs.ignore.warningPrimary')}
          commentsPlaceholder={t('nonCompliance.dialogs.ignore.commentsPlaceholder')}
          submitLabel={t('nonCompliance.dialogs.ignore.submit')}
          cancelLabel={t('nonCompliance.dialogs.ignore.cancel')}
          onSubmit={handleIgnoreSubmit}
          processing={ignoreProcessing}
        />

        <RequireExplanationDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          heading={t('nonCompliance.dialogs.confirm.heading')}
          warningPrimary={t('nonCompliance.dialogs.confirm.warningPrimary')}
          warningSecondary={t('nonCompliance.dialogs.confirm.warningSecondary')}
          commentsPlaceholder={t('nonCompliance.dialogs.confirm.commentsPlaceholder')}
          submitLabel={t('nonCompliance.dialogs.confirm.submit')}
          cancelLabel={t('nonCompliance.dialogs.confirm.cancel')}
          onSubmit={handleConfirmSubmit}
          processing={confirmProcessing}
        />

        <RequireExplanationDialog
          open={offlineRetireOpen}
          onOpenChange={setOfflineRetireOpen}
          heading={t('nonCompliance.dialogs.offlineRetire.heading')}
          warningPrimary={t('nonCompliance.dialogs.offlineRetire.warningPrimary')}
          warningSecondary={t('nonCompliance.dialogs.offlineRetire.warningSecondary')}
          commentsPlaceholder={t('nonCompliance.dialogs.offlineRetire.commentsPlaceholder')}
          submitLabel={t('nonCompliance.dialogs.offlineRetire.submit')}
          cancelLabel={t('nonCompliance.dialogs.offlineRetire.cancel')}
          onSubmit={handleOfflineRetireSubmit}
          processing={offlineRetireProcessing}
        />

        <TaskDayOccurrencesDialog
          instrumentId={record.instrumentId}
          instrumentLabel={record.instrumentLabel}
          serialNumber={record.serialNumber}
          modalState={
            dayOccurrencesModalState
              ? {
                  ...dayOccurrencesModalState,
                  taskNotes,
                  dataFields: dataFields ?? dayOccurrencesModalState.dataFields,
                }
              : null
          }
          onClose={() => setDayOccurrencesModalState(null)}
          dateToLogIdMap={timelineDateStripMap}
          onReviewSuccess={handleTimelineReviewSuccess}
        />
      </Card>
    </div>
  )
}
